import React, { useState, useEffect } from 'react';
import styles from '../../styles/Subscription/SubscriptionModal.module.css';
import { loadIzipaySdk } from "../../js/izipaySdk";
import { GetTokenSession } from "../../js/getTokenSession";
import { getDataOrderDynamic } from "../../js/util";

//  CONFIGURACIÓN IZIPAY (Sin etiquetas de TEST) 
const MERCHANT_CODE = process.env.REACT_APP_IZIPAY_MERCHANT_CODE || "4004353";
const PUBLIC_KEY = process.env.REACT_APP_IZIPAY_PUBLIC_KEY || "VErethUtraQuxas57wuMuquprADrAHAb";
const RENDER_MODE = process.env.REACT_APP_IZIPAY_RENDER_MODE || "popup";
const REDIRECT_URL = process.env.REACT_APP_IZIPAY_REDIRECT_URL || "https://server.punto-web.com/comercio/creceivedemo.asp?p=h1";
const LOGO = process.env.REACT_APP_IZIPAY_APPEARANCE_LOGO || "https://demo-izipay.azureedge.net/test/img/millasb.svg";
// URL que Izipay llamará server-to-server al completar el pago (IPN/webhook)
const IPN_URL =
  process.env.REACT_APP_IZIPAY_IPN_URL ||
  `${process.env.REACT_APP_API_URL || "http://localhost:3001"}/api/payments/izipay/webhook`;


const SubscriptionModal = ({ show, status, onClose, amountToPay = "1.00", currency = "PEN", postalCode }) => {
  // Decodificar datos del usuario desde el JWT almacenado
  const currentUser = (() => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return {};
      return JSON.parse(atob(token.split(".")[1]));
    } catch {
      return {};
    }
  })();
  // Estados para el motor de IziPay
  const [token, setToken] = useState(null);
  const [transactionId, setTxId] = useState(null);
  const [orderNumber, setOrderNumber] = useState(null);
  const [paymentState, setPaymentState] = useState("idle"); // idle | loading_token | ready | paying | done | error

  // Cuando el modal se abre y el usuario debe pagar, pedimos el Token silenciosamente
  useEffect(() => {
    // Solo pedimos token si el modal está visible y el usuario está inactivo
    if (!show || status !== 'inactive') return;

    let cancelled = false;
    const { transactionId: txId, orderNumber: oNum } = getDataOrderDynamic();
    setTxId(txId);
    setOrderNumber(oNum);
    setPaymentState("loading_token");

    GetTokenSession(txId, {
      merchantCode: MERCHANT_CODE,
      publicKey: PUBLIC_KEY,
      orderNumber: oNum,
      amount: amountToPay,
      currency: currency,
    })
      .then((resp) => {
        if (cancelled) return;
        if (resp?.response?.token) {
          setToken(resp.response.token);
          setTxId(txId);
          setOrderNumber(oNum);
          setPaymentState("ready");
        } else {
          console.error("Respuesta sin token:", resp);
          setPaymentState("error");
        }
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Error al obtener token:", err);
        setPaymentState("error");
      });

    return () => { cancelled = true; };
  }, [show, status, amountToPay, currency]);

  // Función que detona el Popup de IziPay
  const handlePay = async () => {
    if (!token) return;
    setPaymentState("paying");
    
    try {
      await loadIzipaySdk();

      const iziConfig = {
        publicKey: PUBLIC_KEY,
        config: {
          transactionId,
          action: "pay",
          merchantCode: MERCHANT_CODE,
          order: {
            orderNumber,
            currency: currency,
            amount: amountToPay,
            processType: "AT",
            merchantBuyerId: currentUser.id || "vitalclic-user",
            dateTimeTransaction: Number(`${Date.now()}000`),
            payMethod: "CARD,QR,YAPE_CODE,PAGO_PUSH",
          },
          card: { brand: "", pan: "" },
          billing: {
            firstName: currentUser.name || "Usuario",
            lastName: currentUser.lastName || "Vitalclic",
            email: currentUser.email || "contacto@vitalclic.com",
            phoneNumber: currentUser.phone || "999999999",
            street: "Av. Salud 123",
            city: "Lima",
            state: "Lima",
            country: "PE",
            postalCode: postalCode || "15001",
            documentType: "DNI",
            document: "12345678",
          },
          render: {
            typeForm: RENDER_MODE === "embed" ? "in-frame" : "pop-up",
            container: "#izipay-payment-container",
          },
          urlRedirect: REDIRECT_URL,
          appearance: { logo: LOGO },
          urlIPN: IPN_URL,
        },
      };

      const izi = new window.Izipay({
        publicKey: iziConfig.publicKey,
        config: iziConfig.config,
      });

      izi.LoadForm({
        authorization: token,
        keyRSA: "RSA",
        callbackResponse: async (response) => {
          console.log("Izipay callbackResponse:", response);
          if (String(response?.code) === "00") {
            setPaymentState("done");
            // Guardar el pago en la base de datos
            try {
              const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";
              await fetch(`${API_URL}/api/payments/izipay/confirm`, {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: localStorage.getItem("token") || "",
                },
                body: JSON.stringify({
                  ...response,
                  merchantBuyerId: currentUser.id,
                }),
              });
            } catch (confirmErr) {
              console.error("Error al confirmar pago:", confirmErr);
            }
            alert("✅ ¡Pago aprobado con éxito! Bienvenido a Vitalclic.");
            onClose(true); // true = pago exitoso, el padre debe re-verificar suscripción
          } else {
            setPaymentState("ready");
            alert(`⚠️ Hubo un problema con el pago (Código: ${response?.code}). Intenta nuevamente.`);
          }
        },
      });
    } catch (err) {
      console.error("Error al abrir checkout:", err);
      setPaymentState("error");
    }
  };

  // 👇 NUEVA FUNCIÓN: SIMULADOR DE PAGO EXITOSO
  const handleSimulateSuccess = () => {
    // 1. Forzamos el estado de éxito
    setPaymentState("done");
    
    // 2. Simulamos la respuesta que daría IziPay si todo sale bien
    const fakeResponse = { code: "00", message: "Simulación Exitosa" };
    console.log("Simulador: Pago aprobado (Bypass)", fakeResponse);
    
    // 3. Mostramos la alerta de éxito
    alert("✅ ¡Pago SIMULADO con éxito! Bienvenido a Vitalclic.");
    
    // 4. Cerramos el modal
    onClose(); 
  };

  if (!show) return null;

  return (
    <div className={styles.modalOverlay}>
      <div className={`${styles.modalContent} text-center`}>
        
        {/* CARA 1: Consultando al backend */}
        {status === 'loading' && (
          <div className="py-4">
            <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem', color: '#53676c' }}></div>
            <h4 className="mt-4 fw-bold" style={{ color: '#53676c' }}>Verificando tu cuenta...</h4>
          </div>
        )}

        {/* CARA 2: Al día (Mensaje de Bienvenida) */}
        {status === 'active' && (
          <div className="py-3">
            <div style={{ fontSize: '4rem', color: '#198754', marginBottom: '10px' }}>✅</div>
            <h3 className="mb-3 fw-bold">¡Bienvenido a Vitalclic!</h3>
            <p className="text-muted mb-4">Tu cuota está al día. Gracias por confiar en nosotros.</p>
            <button type="button" className="btn btn-success btn-lg w-100 fw-bold" onClick={onClose}>
              Ingresar al sistema
            </button>
          </div>
        )}

        {/* CARA 3: En deuda (Cartel de Cobro con Botón IziPay) */}
        {status === 'inactive' && (
          <div className="py-3">
            <button
              onClick={onClose}
              style={{
                position: 'absolute', top: '12px', right: '16px',
                background: 'none', border: 'none', fontSize: '1.4rem',
                cursor: 'pointer', color: '#98a2b3', lineHeight: 1,
              }}
              aria-label="Cerrar"
            >
              ×
            </button>
            <div style={{ fontSize: '4rem', color: '#dc3545', marginBottom: '10px' }}>⚠️</div>
            <h3 className="mb-3 fw-bold">¡Atención! Cuota pendiente</h3>
            <p className="text-muted mb-3">Para utilizar todos los beneficios, necesitas regularizar tu membresía.</p>
            
            <div className="alert alert-warning mb-4">
              Total a pagar: <strong>{currency} {amountToPay}</strong>
            </div>

            <button 
              type="button" 
              className="btn btn-primary btn-lg w-100 fw-bold mb-3"
              onClick={handlePay}
              disabled={paymentState === "loading_token" || paymentState === "paying" || paymentState === "error"}
              style={{ backgroundColor: '#53676c', borderColor: '#53676c' }}
            >
              {paymentState === "loading_token" && "Preparando pasarela segura..."}
              {paymentState === "ready" && "Pagar con IziPay"}
              {paymentState === "paying" && "Abriendo ventana de pago..."}
              {paymentState === "error" && "Error de conexión (Reintentar)"}
            </button>

            {/* 👇 EL BOTÓN MÁGICO (Solo se muestra en localhost) */}
            {window.location.hostname === "localhost" && (
              <button 
                type="button" 
                className="btn btn-outline-success w-100 fw-bold"
                onClick={handleSimulateSuccess}
              >
                🧪 Simular Pago Exitoso (Dev Only)
              </button>
            )}
          </div>
        )}

      </div>
      <div id="izipay-payment-container" style={{ display: 'none' }}></div>
    </div>
  );
};

export default SubscriptionModal;



